import { useState } from "react";
import { AppDialog, AppDialogScroll } from "@/components/ui/app-dialog";
import { StepOne } from "./StepOne";
import { StepLocation } from "./StepLocation";
import { StepTwo } from "./StepTwo";
import { StepThree } from "./StepThree";
import { useUserProfile } from "@/hooks/useUserProfile";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface OnboardingWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (firstGoal?: { title: string; category: string }) => void;
}

const TOTAL_STEPS = 4;

export function OnboardingWizard({ open, onOpenChange, onComplete }: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedAvatar, setSelectedAvatar] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [selectedGoal, setSelectedGoal] = useState<{ title: string; category: string } | null>(null);
  const { updateProfile } = useUserProfile();
  const { user } = useAuth();

  const markOnboardingComplete = async () => {
    localStorage.setItem("onboarding_completed", "true");
    localStorage.setItem("onboarding_completed_at", String(Date.now()));
    if (user) {
      await supabase.from('profiles').update({ onboarding_completed: true }).eq('id', user.id);
    }
  };

  /**
   * Closing early must never lose work: persist whatever was picked so far,
   * and always mark onboarding complete so the wizard never repeats.
   */
  const handleSkip = async () => {
    const partial = {
      ...(selectedAvatar ? { avatar: selectedAvatar } : {}),
      ...(selectedColor ? { theme_color: selectedColor } : {}),
      ...(selectedLocation ? { location: selectedLocation } : {}),
      ...(selectedInterests.length > 0 ? { interests: selectedInterests } : {}),
    };
    if (Object.keys(partial).length > 0) {
      await updateProfile(partial);
      if (selectedAvatar) localStorage.setItem("user_avatar", selectedAvatar);
      if (selectedColor) localStorage.setItem("user_color", selectedColor);
    }
    await markOnboardingComplete();
    onComplete();
  };

  const handleStep1Complete = async (avatar: string, color: string) => {
    setSelectedAvatar(avatar);
    setSelectedColor(color);
    // Save avatar + colour immediately — never lose it if the user bails out.
    localStorage.setItem("user_avatar", avatar);
    localStorage.setItem("user_color", color);
    await updateProfile({ avatar, theme_color: color });
    setCurrentStep(2);
  };

  const handleStep2Complete = (location: string, interests: string[]) => {
    setSelectedLocation(location);
    setSelectedInterests(interests);
    setCurrentStep(3);
  };

  const handleStep3Complete = (goal: { title: string; category: string }) => {
    setSelectedGoal(goal);
    setCurrentStep(4);
  };

  const handleFinish = async () => {
    // Save to database (and localStorage as fallback)
    await updateProfile({
      avatar: selectedAvatar,
      theme_color: selectedColor,
      ...(selectedLocation ? { location: selectedLocation } : {}),
      ...(selectedInterests.length > 0 ? { interests: selectedInterests } : {}),
    });
    
    await markOnboardingComplete();
    if (selectedAvatar) localStorage.setItem("user_avatar", selectedAvatar);
    if (selectedColor) localStorage.setItem("user_color", selectedColor);
    
    // Pass the goal back to create it
    onComplete(selectedGoal || undefined);
  };

  return (
    <AppDialog
      open={open}
      onClose={handleSkip}
      size="lg"
      ariaTitle="Welcome to GoalDigger"
    >
      <AppDialogScroll>
        {/* Progress bar */}
        <div className="sticky top-0 left-0 right-0 h-2 bg-muted z-20">
          <div
            className="h-full bg-gradient-to-r from-[#F97316] to-[#D946EF] transition-all duration-500"
            style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
          />
        </div>

        {/* Step content */}
        <div className="p-6 sm:p-8 pt-10 sm:pt-12 pb-6">
          {currentStep === 1 && (
            <StepOne onNext={handleStep1Complete} />
          )}
          {currentStep === 2 && (
            <StepLocation onNext={handleStep2Complete} onBack={() => setCurrentStep(1)} />
          )}
          {currentStep === 3 && (
            <StepTwo onNext={handleStep3Complete} onBack={() => setCurrentStep(2)} />
          )}
          {currentStep === 4 && (
            <StepThree
              avatar={selectedAvatar}
              color={selectedColor}
              goal={selectedGoal?.title || ""}
              onFinish={handleFinish}
            />
          )}
        </div>
      </AppDialogScroll>
    </AppDialog>
  );
}
